/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useMemo, useState } from 'react';
import { StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { MasterFilterCompactPicker } from '../../../../shared/filters/MasterFilterCompactPicker'
import { MasterFilterSheetModal } from '../../../../shared/filters/MasterFilterSheetModal'
import type { MasterFilterOption } from '../../../../shared/filters/MasterFilterSheetModal';
import { spacing } from '../../../../shared/theme/spacing'
import { typography } from '../../../../shared/theme/typography'
import { ticketColors } from '../../../../shared/theme/ticketColors';
import type { EmployeeRecord } from '../../../store/useEmployeesStore';
import { resolveBranchDisplayName } from './useEmployeePermissions';

type Props = {
  employee: EmployeeRecord;
  activeBranchId: string;
  branchNameById: Record<string, string>;
  peerEmployees: EmployeeRecord[];
  onCopyFromBranch: (sourceBranchId: string) => void;
  onCopyFromEmployee: (sourceEmployeeId: string) => void;
  canEdit: boolean;
};

export function EmployeePermissionCopyPanel({
  employee,
  activeBranchId,
  branchNameById,
  peerEmployees,
  onCopyFromBranch,
  onCopyFromEmployee,
  canEdit,
}: Props) {
  const [branchSheetOpen, setBranchSheetOpen] = useState(false);
  const [employeeSheetOpen, setEmployeeSheetOpen] = useState(false);
  const [selectedBranchId, setSelectedBranchId] = useState<string | null>(null);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string | null>(null);

  const otherBranches = employee.branchAssignments.filter((a) => a.branchId !== activeBranchId);
  const branchOptions: MasterFilterOption[] = useMemo(
    () =>
      otherBranches.map((a) => ({
        id: a.branchId,
        name: resolveBranchDisplayName(
          branchNameById,
          a.branchId,
          a.branchName ?? 'Sucursal'
        ),
      })),
    [otherBranches, branchNameById]
  );

  const employeeOptions: MasterFilterOption[] = useMemo(
    () =>
      peerEmployees
        .filter((e) => e.id !== employee.id)
        .map((e) => ({ id: e.id, name: e.name })),
    [peerEmployees, employee.id]
  );

  const selectedBranchLabel =
    branchOptions.find((b) => b.id === selectedBranchId)?.name ?? 'Elegir sucursal';
  const selectedEmployeeLabel =
    employeeOptions.find((e) => e.id === selectedEmployeeId)?.name ?? 'Elegir persona';

  if (!canEdit) return null;
  if (branchOptions.length === 0 && employeeOptions.length === 0) return null;

  return (
    <View style={styles.wrap} testID="perfil.team.detail.copy-permissions">
      <Text style={styles.title}>Copiar permisos</Text>
      <Text style={styles.hint}>
        Traé permisos de otra sucursal de {employee.name} o de otra persona en esta sucursal.
      </Text>

      {branchOptions.length > 0 ? (
        <View style={styles.row}>
          <MasterFilterCompactPicker
            caption="Desde sucursal"
            value={selectedBranchLabel}
            active={selectedBranchId != null}
            dense
            onPress={() => setBranchSheetOpen(true)}
          />
          <TouchableOpacity
            style={styles.actionBtn}
            onPress={() => selectedBranchId && onCopyFromBranch(selectedBranchId)}
            disabled={!selectedBranchId}
            accessibilityRole="button"
            accessibilityLabel="Copiar permisos desde otra sucursal"
          >
            <Text style={[styles.actionLink, !selectedBranchId && styles.actionLinkDisabled]}>
              Copiar
            </Text>
          </TouchableOpacity>
        </View>
      ) : null}

      {employeeOptions.length > 0 ? (
        <View style={styles.row}>
          <MasterFilterCompactPicker
            caption="Desde persona"
            value={selectedEmployeeLabel}
            active={selectedEmployeeId != null}
            dense
            onPress={() => setEmployeeSheetOpen(true)}
          />
          <TouchableOpacity
            style={styles.actionBtn}
            onPress={() => selectedEmployeeId && onCopyFromEmployee(selectedEmployeeId)}
            disabled={!selectedEmployeeId}
            accessibilityRole="button"
            accessibilityLabel="Copiar permisos desde otra persona"
          >
            <Text style={[styles.actionLink, !selectedEmployeeId && styles.actionLinkDisabled]}>
              Copiar
            </Text>
          </TouchableOpacity>
        </View>
      ) : null}

      <MasterFilterSheetModal
        visible={branchSheetOpen}
        title="Copiar desde sucursal"
        options={branchOptions}
        selectedId={selectedBranchId}
        onSelect={setSelectedBranchId}
        onClose={() => setBranchSheetOpen(false)}
        showAllOption={false}
      />
      <MasterFilterSheetModal
        visible={employeeSheetOpen}
        title="Copiar desde persona"
        options={employeeOptions}
        selectedId={selectedEmployeeId}
        onSelect={setSelectedEmployeeId}
        onClose={() => setEmployeeSheetOpen(false)}
        showAllOption={false}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  wrap: {
    gap: spacing.sm,
    padding: spacing.md,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper2,
  },
  title: {
    ...typography.caption,
    fontWeight: '700',
    color: ticketColors.ink,
    textTransform: 'uppercase',
    letterSpacing: 0.35,
  },
  hint: {
    ...typography.caption,
    color: ticketColors.ink2,
    lineHeight: 17,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    gap: spacing.sm,
    flexWrap: 'wrap',
  },
  actionBtn: {
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.xs,
  },
  actionLink: {
    ...typography.caption,
    color: ticketColors.gold,
    fontWeight: '800',
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.xs,
  },
  actionLinkDisabled: {
    color: ticketColors.ink3,
  },
});
